import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import HistoryChats from "../../Templates/HistoryChats";
import axios from "axios";
import Swal from "sweetalert2";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import PublishableService from "../../../services/Publishable/PublishableService";
import UserService from "../../../services/User/UserService";

const HistoryChatsPage = () => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredChats, setFilteredChats] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [chatNames, setChatNames] = useState({});
  const itemsPerPage = 10;

  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const accessToken = keycloak.token;
  const navigate = useNavigate();
  const location = useLocation();

  const updateURLParams = (query, agentType) => {
    const params = new URLSearchParams();
    if (query) params.set("query", query);
    if (agentType) params.set("agentType", agentType);
    navigate({ search: params.toString() });
  };

  const getParamsFromURL = () => {
    const params = new URLSearchParams(location.search);
    const query = params.get("query") || "";
    const agentType = params.get("agentType") || "";
    setSearchQuery(query);
    handleFilterByAgent(agentType);
    return { query, agentType };
  };

  const handleFilterByAgent = (agentType) => {
    updateURLParams(searchQuery, agentType);
    const results = agentType
      ? chats.filter((chat) => chat.contextName === agentType)
      : chats;
    setFilteredChats(results);
    setCurrentPage(1);
  };

  const editAgentName = async (id) => {
    const { value: newName } = await Swal.fire({
      title: t("history.editHistoryModalTitle"),
      input: "text",
      inputPlaceholder: t("history.editHistoryModalInputPlaceholder"),
      inputAttributes: {
        maxlength: 120,
      },
      showCancelButton: true,
      confirmButtonText: t("history.editHistoryModalSave"),
      cancelButtonText: t("history.editHistoryModalCancel"),
      confirmButtonColor: "#0e2238",
      cancelButtonColor: "#6c757d",
    });

    if (newName) {
      try {
        await axios.patch(
          `${process.env.REACT_APP_AUTH_API_URL}/chats/update-title/${id}`,
          { title: newName },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );


        fetchChats(
          searchQuery,
          new URLSearchParams(location.search).get("agentType")
        );
      } catch (error) {
        console.error(t("history.editHistoryErroToUpdateTitle"), error);
      }
    }
  };

  const removeAgentName = async (id) => {


      try {
        await axios.patch(
          `${process.env.REACT_APP_AUTH_API_URL}/chats/delete/${id}`,
          {},
          { headers: { Authorization: `Bearer ${keycloak.token}` } }
        );
        fetchChats(searchQuery);
      } catch (error) {
        console.error(t("history.removeHistoryError"), error);
      }
    
  };

  const fetchChats = async (query = "", agentType = "") => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_AUTH_API_URL}/chats/user`,
        {
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
          },
          params: {
            filter: query,
          },
        }
      );
      const dataFromBackend = response.data;

      const filteredData = dataFromBackend
        .filter((chat) =>
          chat.title.toLowerCase().includes(query.toLowerCase())
        )
        .filter((chat) => (agentType ? chat.contextName === agentType : true));

      setChats(dataFromBackend);
      setFilteredChats(filteredData);
    } catch (error) {
      console.error(t("history.backendGetDataError"), error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateURLParams(
      query,
      new URLSearchParams(location.search).get("agentType")
    );
    fetchChats(query, new URLSearchParams(location.search).get("agentType"));
  };

  useEffect(() => {
    const { query, agentType } = getParamsFromURL();
    fetchChats(query, agentType);
  }, [accessToken, location.search]);

  useEffect(() => {
    const fetchProjectNames = async () => {
      try {
        if (!accessToken) return;
        const user = await UserService.getUserByToken(accessToken);
        
        const publishables = await PublishableService.getPublishablesByUser(
          user.id,
          accessToken
        );
        
        const projectNameMap = publishables.reduce((acc, pub) => {
          const { project } = pub;
          if (project && project.id && !acc[project.id]) {
            acc[project.id] = project.name;
          }
          return acc;
        }, {});

        setChatNames(projectNameMap);
      } catch (error) {
        console.error("Error fetching project names for history chats:", error);
      }
    };

    fetchProjectNames();
  }, [accessToken]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentChats = filteredChats.slice(
    startIndex,
    startIndex + itemsPerPage
  );
  const totalPages = Math.ceil(filteredChats.length / itemsPerPage);

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  return (
    <>
      <HistoryChats
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        handleFilterByAgent={handleFilterByAgent}
        chatNames={chatNames}
        loading={loading}
        filteredChats={currentChats}
        editAgentName={editAgentName}
        removeAgentName={removeAgentName}
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
      />
    </>
  );
};

export default HistoryChatsPage;